// iter158 — BFR Email Screen: composer + history + Gmail status banner.
// Routes to backend `/api/admin/email/*` which proxies to the CRM Gmail
// Integration. NO Google OAuth or SMTP in this app.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Mail, Send, RefreshCw, XCircle, CheckCircle2, Eye,
  Loader2, AlertCircle, MailCheck, MailX, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

import { bfrEmailAPI } from '../../services/api';
import {
  EMAIL_TEMPLATES,
  REJECTION_REASONS,
  AVAILABLE_VARIABLES,
  renderTemplate,
  notifyEmailError,
} from './emailTemplates';

const initialForm = {
  template_type: 'custom',
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  body: '',
  rejection_reason: '',
  rejection_reason_custom: '',
};

// ---------------------------------------------------------------------------
// Gmail connection banner
// ---------------------------------------------------------------------------

const GmailBanner = ({ status, onRefresh, loading }) => {
  const connected = !!status?.connected;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-3 ${
        connected
          ? 'bg-[#1B2A7B]/10 border-[#1B2A7B]/40 text-[#EEF1FC]'
          : 'bg-[#1B2A7B]/10 border-[#1B2A7B]/40 text-slate-600'
      }`}
      data-testid="gmail-status-banner"
    >
      {connected ? (
        <MailCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
      ) : (
        <MailX className="w-5 h-5 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">
          {connected ? 'Gmail Connected' : 'Gmail Not Connected'}
        </p>
        <p className="text-xs opacity-90 mt-0.5" data-testid="gmail-status-message">
          {connected
            ? `Sending from: ${status?.sender_email || 'connected Gmail account'}`
            : (status?.message ||
                'Please connect Gmail from CRM Settings → Gmail Integration.')}
        </p>
      </div>
      <button
        onClick={onRefresh}
        className="text-xs px-2.5 py-1 rounded-md border border-current/30 hover:bg-current/10"
        data-testid="gmail-status-refresh"
        disabled={loading}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refresh'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

const Composer = ({ bookingId, gmailStatus, onSent }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rendered, setRendered] = useState({ subject: '', body: '', variables: {} });
  const [previewLoading, setPreviewLoading] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // On template change, pre-fill subject/body (editable).
  const applyTemplate = useCallback((type) => {
    const t = EMAIL_TEMPLATES[type] || EMAIL_TEMPLATES.custom;
    setForm((f) => ({
      ...f,
      template_type: type,
      subject: t.subject,
      body: t.body,
    }));
  }, []);

  useEffect(() => {
    // First mount: keep custom + blank so admin can pick a quick action.
  }, []);

  const effectiveRejectionReason = useMemo(() => {
    if (form.template_type !== 'ota_rejection') return '';
    return form.rejection_reason === 'Other'
      ? (form.rejection_reason_custom || '').trim()
      : form.rejection_reason;
  }, [form.template_type, form.rejection_reason, form.rejection_reason_custom]);

  const buildExtras = () => {
    const e = {};
    if (effectiveRejectionReason) e.rejection_reason = effectiveRejectionReason;
    return e;
  };

  const openPreview = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error('Subject and body are required.');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await bfrEmailAPI.resolveTemplate({
        booking_id: bookingId || undefined,
        subject: form.subject,
        body: form.body,
        extras: buildExtras(),
      });
      setRendered(res);
      setPreviewOpen(true);
    } catch (err) {
      // Fallback to client-side render — booking variables will be blank but
      // manual/rejection_reason values still render for a usable preview.
      setRendered({
        subject: renderTemplate(form.subject, buildExtras()),
        body: renderTemplate(form.body, buildExtras()),
        variables: buildExtras(),
      });
      setPreviewOpen(true);
      notifyEmailError(err, 'Preview render failed — showing local fallback.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const send = async () => {
    if (!gmailStatus?.connected) {
      toast.error('Gmail is not connected — connect it from CRM Settings first.');
      return;
    }
    if (!form.to.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error('Recipient, subject and body are required.');
      return;
    }
    if (form.template_type === 'ota_rejection' && !effectiveRejectionReason) {
      toast.error('Rejection reason is required for rejection emails.');
      return;
    }
    setSending(true);
    try {
      const res = await bfrEmailAPI.send({
        to: form.to.trim(),
        cc: form.cc.trim() || undefined,
        bcc: form.bcc.trim() || undefined,
        subject: form.subject,
        body: form.body,
        template_type: form.template_type,
        booking_id: bookingId || undefined,
        rejection_reason: effectiveRejectionReason || undefined,
      });
      if (res?.success) {
        toast.success('Email sent.');
        setForm(initialForm);
        setPreviewOpen(false);
        onSent?.();
      } else {
        toast.error(`Send failed: ${res?.error || 'Unknown error'}`);
        onSent?.();
      }
    } catch (err) {
      notifyEmailError(err, 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full bg-white border border-slate-200 text-slate-900 text-sm rounded-lg px-3 py-2 outline-none focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B]/30';

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="bfr-email-composer">
      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(EMAIL_TEMPLATES).map(([k, t]) => (
          <button
            key={k}
            onClick={() => applyTemplate(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              form.template_type === k
                ? 'bg-[#1B2A7B]/20 text-[#1B2A7B] border-[#1B2A7B]'
                : 'bg-slate-100 text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`quick-action-${k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 text-sm">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">From</label>
          <div className="text-slate-600 px-3 py-2 bg-white border border-slate-200 rounded-lg" data-testid="composer-from">
            {gmailStatus?.connected ? (gmailStatus.sender_email || 'Connected Gmail') : 'Gmail not connected'}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">To *</label>
            <input
              value={form.to}
              onChange={(e) => setField('to', e.target.value)}
              className={inputCls}
              placeholder="ops@ota.com"
              data-testid="composer-to"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">CC</label>
            <input
              value={form.cc}
              onChange={(e) => setField('cc', e.target.value)}
              className={inputCls}
              data-testid="composer-cc"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">BCC</label>
            <input
              value={form.bcc}
              onChange={(e) => setField('bcc', e.target.value)}
              className={inputCls}
              data-testid="composer-bcc"
            />
          </div>
        </div>

        {form.template_type === 'ota_rejection' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Rejection Reason *</label>
              <select
                value={form.rejection_reason}
                onChange={(e) => setField('rejection_reason', e.target.value)}
                className={inputCls}
                data-testid="composer-rejection-reason"
              >
                <option value="">Select…</option>
                {REJECTION_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            {form.rejection_reason === 'Other' && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Custom Reason</label>
                <input
                  value={form.rejection_reason_custom}
                  onChange={(e) => setField('rejection_reason_custom', e.target.value)}
                  className={inputCls}
                  data-testid="composer-rejection-custom"
                />
              </div>
            )}
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Subject *</label>
          <input
            value={form.subject}
            onChange={(e) => setField('subject', e.target.value)}
            className={inputCls}
            data-testid="composer-subject"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Body *</label>
            <details className="text-[11px]">
              <summary className="text-slate-500 cursor-pointer hover:text-slate-600 select-none">
                Available variables <ChevronDown className="inline w-3 h-3" />
              </summary>
              <div className="absolute right-4 mt-1 max-w-xs bg-white border border-slate-200 rounded-lg p-2 text-slate-500 z-10">
                {AVAILABLE_VARIABLES.map((v) => (
                  <button
                    key={v}
                    onClick={() => setField('body', `${form.body}{{${v}}}`)}
                    className="text-left w-full px-1 py-0.5 hover:bg-slate-100 rounded font-mono text-[11px]"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </details>
          </div>
          <textarea
            value={form.body}
            onChange={(e) => setField('body', e.target.value)}
            rows={10}
            className={`${inputCls} font-mono text-xs leading-relaxed`}
            data-testid="composer-body"
          />
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-1">
          <button
            onClick={() => setForm(initialForm)}
            className="px-3 py-2 text-xs rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
            data-testid="composer-cancel"
          >
            Cancel
          </button>
          <button
            onClick={openPreview}
            disabled={previewLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-slate-200 text-slate-700 hover:border-slate-500"
            data-testid="composer-preview-btn"
          >
            {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Preview
          </button>
          <button
            onClick={send}
            disabled={sending || !gmailStatus?.connected}
            className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-lg bg-[#1B2A7B] text-white font-semibold hover:bg-[#152163] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="composer-send-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send Email
          </button>
        </div>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="preview-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <p className="text-slate-900 text-sm font-semibold">Email Preview</p>
              <button onClick={() => setPreviewOpen(false)} className="text-slate-500 hover:text-[#1B2A7B]" data-testid="preview-close">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">From</p>
                <p className="text-slate-700">{gmailStatus?.sender_email || 'Connected Gmail'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">To</p>
                <p className="text-slate-700">{form.to || <span className="text-red-400">missing</span>}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Subject</p>
                <p className="text-[#1B2A7B] font-semibold" data-testid="preview-subject">{rendered.subject || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Body</p>
                <pre className="text-slate-700 whitespace-pre-wrap font-sans text-xs bg-white border border-slate-200 rounded-lg p-3" data-testid="preview-body">
                  {rendered.body || '—'}
                </pre>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex justify-end gap-2">
              <button
                onClick={() => setPreviewOpen(false)}
                className="px-3 py-2 text-xs rounded-lg bg-slate-100 text-slate-600"
              >
                Close
              </button>
              <button
                onClick={send}
                disabled={sending || !gmailStatus?.connected}
                className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-lg bg-[#1B2A7B] text-white font-semibold hover:bg-[#152163] disabled:opacity-50"
                data-testid="preview-send-btn"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// History table
// ---------------------------------------------------------------------------

const HistoryTable = ({ rows, onRetry, retryingId }) => {
  if (!rows?.length) {
    return (
      <div className="text-center py-10 text-slate-500 text-sm" data-testid="history-empty">
        No emails sent yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="history-table">
      <table className="w-full text-xs">
        <thead className="border-b border-slate-200 text-slate-500 uppercase tracking-wider">
          <tr className="text-left">
            <th className="px-3 py-2">Sent</th>
            <th className="px-3 py-2">To</th>
            <th className="px-3 py-2">Subject</th>
            <th className="px-3 py-2">Booking</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-200 align-top" data-testid={`history-row-${r.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{(r.sent_at || '').slice(0, 19).replace('T', ' ')}</td>
              <td className="px-3 py-2 max-w-[180px] truncate">{r.to}</td>
              <td className="px-3 py-2 max-w-[240px] truncate">{r.subject}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{r.booking_id || '—'}</td>
              <td className="px-3 py-2">{r.template_type}</td>
              <td className="px-3 py-2">
                {r.status === 'sent' ? (
                  <span className="inline-flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Sent
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-red-400" title={r.error_message}>
                    <AlertCircle className="w-3.5 h-3.5" /> Failed
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                {r.status === 'failed' && (
                  <button
                    onClick={() => onRetry(r.id)}
                    disabled={retryingId === r.id}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:border-slate-500"
                    data-testid={`history-retry-${r.id}`}
                  >
                    {retryingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Retry
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

const AdminEmailScreen = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const bookingId = params.get('booking_id') || '';

  const [gmailStatus, setGmailStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      // NOTE: `localApi` response interceptor already returns `response.data`
      // directly, so the call resolves to the JSON body (never destructure `.data`).
      const res = await bfrEmailAPI.gmailStatus();
      setGmailStatus(res);
    } catch (err) {
      setGmailStatus({ connected: false, sender_email: '', message: 'Failed to reach backend.' });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await bfrEmailAPI.history({ booking_id: bookingId || undefined });
      setHistory(res?.rows || []);
    } catch (err) {
      notifyEmailError(err, 'Failed to load email history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    loadStatus();
    loadHistory();
  }, [loadStatus, loadHistory]);

  const onRetry = async (id) => {
    setRetryingId(id);
    try {
      const res = await bfrEmailAPI.retry(id);
      if (res?.success) {
        toast.success('Retry sent.');
      } else {
        toast.error('Retry still failed.');
      }
      loadHistory();
    } catch (err) {
      notifyEmailError(err, 'Retry failed.');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-[#152163] sticky top-0 bg-[#1B2A7B] z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-white/10"
            data-testid="email-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-white" />
            <h1 className="text-white font-semibold text-sm">BFR Email</h1>
            {bookingId && (
              <span className="text-[11px] px-2 py-0.5 bg-white/15 rounded-full text-white font-mono">
                Booking {bookingId}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-5 pb-28">
        <GmailBanner status={gmailStatus} onRefresh={loadStatus} loading={statusLoading} />

        <Composer
          bookingId={bookingId}
          gmailStatus={gmailStatus}
          onSent={loadHistory}
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-700 font-semibold">Email History</p>
            <button
              onClick={loadHistory}
              className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-slate-500 flex items-center gap-1"
              data-testid="history-refresh"
            >
              {historyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-2">
            <HistoryTable rows={history} onRetry={onRetry} retryingId={retryingId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailScreen;
